/**
 * PerformanceUpload.js
 * Download a filtered student Excel template, fill in KPI scores, upload it back.
 */
import React, { useEffect, useState, useRef } from "react";
import { FaCloudUploadAlt } from "react-icons/fa";
import { getBrandColors } from "./ThemeContext";
import PageHeaderIcon from "./components/PageHeaderIcon";
import API_BASE from "./apiConfig";
import { useSort } from "./hooks/useSort";
import { useDateTimeFormat } from "./utils/formatters";

const BASE = API_BASE;

// ── Colour tokens ──────────────────────────────────────────────────────────────
const C = {
  get navy() { return getBrandColors().blue; },
  blue:        "#0E6FC4",
  blueLight:   "#E8F0FA",
  green:       "#16a34a",
  greenLight:  "#f0fdf4",
  get amber() { return getBrandColors().orange; },
  amberLight:  "#fffbeb",
  get red() { return getBrandColors().red; },
  redLight:    "#fef2f2",
  border:      "#D1D5DB",
  bg:          "#F3F6FA",
  card:        "#FFFFFF",
  textPrimary: "#111827",
  textMuted:   "#6B7280",
};

// ── Small helpers ──────────────────────────────────────────────────────────────
function Label({ children }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 700, color: C.textMuted,
                  textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>
      {children}
    </div>
  );
}

function Select({ value, onChange, children, disabled }) {
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      disabled={disabled}
      style={{
        width: "100%", padding: "8px 10px", borderRadius: 6,
        border: `1px solid ${C.border}`, fontSize: 13, color: C.textPrimary,
        background: disabled ? "#f9fafb" : C.card, cursor: disabled ? "default" : "pointer",
      }}
    >
      {children}
    </select>
  );
}

function Btn({ onClick, disabled, children, variant = "primary", small }) {
  const bg = {
    primary: C.navy, success: C.green, danger: C.red, ghost: "transparent",
  }[variant];
  const fg = variant === "ghost" ? C.navy : "#fff";
  const border = variant === "ghost" ? `1.5px solid ${C.navy}` : "none";
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: small ? "6px 14px" : "9px 20px",
        background: disabled ? "#CBD5E1" : bg,
        color: disabled ? "#94A3B8" : fg,
        border: disabled ? "none" : border,
        borderRadius: 7, fontSize: small ? 12 : 13, fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer",
        display: "inline-flex", alignItems: "center", gap: 6,
      }}
    >
      {children}
    </button>
  );
}

function Badge({ children, color }) {
  return (
    <span style={{
      background: color + "22", color, border: `1px solid ${color}55`,
      borderRadius: 12, padding: "2px 10px", fontSize: 11, fontWeight: 600,
    }}>
      {children}
    </span>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function PerformanceUpload({ currentUser }) {
  const { fmtDate, fmtDateTime } = useDateTimeFormat();
  const [filters, setFilters]           = useState(null);
  const [filterBatchDay, setFilterBatchDay]   = useState("");
  const [filterProgram, setFilterProgram]     = useState("");
  const [filterLevel, setFilterLevel]         = useState("");
  const [filterAge, setFilterAge]             = useState("");

  const [sessions, setSessions]         = useState([]);
  const [uploading, setUploading]       = useState(false);
  const [downloading, setDownloading]   = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadError, setUploadError]   = useState(null);
  const [dragOver, setDragOver]         = useState(false);

  const [sessionLabel, setSessionLabel] = useState("");
  const [sessionDate, setSessionDate]   = useState(new Date().toISOString().slice(0, 10));

  // Default assessment state
  const [defaultLabel, setDefaultLabel]   = useState("");
  const [defaultDate, setDefaultDate]     = useState(new Date().toISOString().slice(0, 10));
  const [defaultRunning, setDefaultRunning] = useState(false);
  const [defaultResult, setDefaultResult]   = useState(null);
  const [defaultError, setDefaultError]     = useState(null);
  const [showDefaultConfirm, setShowDefaultConfirm] = useState(false);

  const fileInputRef = useRef();

  const { sorted: sortedSessions, toggleSort: toggleSessionSort, sortArrow: sessionSortArrow } = useSort(sessions, null);

  // Load filter options + session history
  useEffect(() => {
    fetch(`${BASE}/perf-upload/filters`)
      .then(r => r.json())
      .then(setFilters)
      .catch(() => {});
    loadSessions();
  }, []);

  function loadSessions() {
    fetch(`${BASE}/perf-upload/sessions`)
      .then(r => r.json())
      .then(setSessions)
      .catch(() => {});
  }

  // ── Download template ────────────────────────────────────────────────────────
  async function handleDownload() {
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      if (filterBatchDay !== "") params.set("batch_day",    filterBatchDay);
      if (filterProgram)         params.set("program_id",   filterProgram);
      if (filterLevel)           params.set("level_id",     filterLevel);
      if (filterAge)             params.set("age_category", filterAge);

      const res = await fetch(`${BASE}/perf-upload/template?${params}`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: "Download failed." }));
        alert(err.detail || "Download failed.");
        return;
      }
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = `performance_template_${new Date().toISOString().slice(0,10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  }

  // ── Upload ───────────────────────────────────────────────────────────────────
  async function handleFile(file) {
    if (!file) return;
    if (!file.name.endsWith(".xlsx")) {
      setUploadError("Only .xlsx files are supported.");
      return;
    }
    if (!sessionLabel.trim()) {
      setUploadError("Please enter a session label before uploading (e.g. 'July 2026 Assessment').");
      return;
    }
    setUploading(true);
    setUploadResult(null);
    setUploadError(null);

    const formData = new FormData();
    formData.append("file", file);
    const params = new URLSearchParams({
      session_label: sessionLabel.trim(),
      session_date:  sessionDate,
      uploaded_by:   currentUser?.login_id || currentUser?.username || "admin",
    });

    try {
      const res = await fetch(`${BASE}/perf-upload/upload?${params}`, {
        method: "POST",
        body:   formData,
      });
      const data = await res.json();
      if (!res.ok) {
        setUploadError(data.detail || "Upload failed.");
      } else {
        setUploadResult(data);
        loadSessions();
        setSessionLabel("");
      }
    } catch {
      setUploadError("Network error — could not connect to server.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  // ── Apply default scores ─────────────────────────────────────────────────────
  async function handleApplyDefaults() {
    setShowDefaultConfirm(false);
    if (!defaultLabel.trim()) {
      setDefaultError("Please enter a session label.");
      return;
    }
    setDefaultRunning(true);
    setDefaultResult(null);
    setDefaultError(null);
    const params = new URLSearchParams({
      session_label: defaultLabel.trim(),
      session_date:  defaultDate,
      uploaded_by:   currentUser?.login_id || currentUser?.username || "admin",
    });
    try {
      const res  = await fetch(`${BASE}/perf-upload/apply-defaults?${params}`, { method: "POST" });
      const data = await res.json();
      if (!res.ok) {
        setDefaultError(data.detail || "Failed to apply default scores.");
      } else {
        setDefaultResult(data);
        loadSessions();
        setDefaultLabel("");
      }
    } catch {
      setDefaultError("Network error — could not connect to server.");
    } finally {
      setDefaultRunning(false);
    }
  }

  function onFileChange(e) {
    handleFile(e.target.files[0]);
  }
  function onDrop(e) {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files[0]);
  }

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: "24px 28px", background: C.bg, minHeight: "100dvh" }}>

      {/* Page header */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 24 }}>
        <PageHeaderIcon icon={<FaCloudUploadAlt size={20} color={getBrandColors().blueDark} />} />
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: C.navy, margin: 0 }}>
            Upload Performance Data
          </h2>
          <div style={{ fontSize: 13, color: C.textMuted, marginTop: 2 }}>
            Download a filtered student template → fill in KPI scores → upload back to i-Track
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>

        {/* ── LEFT: Download template ─────────────────────────────────────── */}
        <div style={{ background: C.card, borderRadius: 12, border: `1px solid ${C.border}`,
                      padding: 24, display: "flex", flexDirection: "column", gap: 18 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.navy, display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 20 }}>📥</span> Step 1 — Download Template
          </div>

          <div style={{ fontSize: 12, color: C.textMuted, background: C.blueLight,
                        borderRadius: 8, padding: "10px 14px", lineHeight: 1.6 }}>
            Filter students, then download an Excel file pre-filled with their names.
            KPI columns are auto-generated from your configured performance indicators.
          </div>

          {/* Filters */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div>
              <Label>Batch Day</Label>
              <Select value={filterBatchDay} onChange={setFilterBatchDay}>
                <option value="">All Days</option>
                {(filters?.batch_days || []).map(d => (
                  <option key={d.value} value={d.value}>{d.label}</option>
                ))}
              </Select>
            </div>
            <div>
              <Label>Program</Label>
              <Select value={filterProgram} onChange={setFilterProgram}>
                <option value="">All Programs</option>
                {(filters?.programs || []).map(p => (
                  <option key={p.program_id} value={p.program_id}>{p.program_name}</option>
                ))}
              </Select>
            </div>
            <div>
              <Label>Skill Level</Label>
              <Select value={filterLevel} onChange={setFilterLevel}>
                <option value="">All Levels</option>
                {(filters?.levels || []).map(l => (
                  <option key={l.level_id} value={l.level_id}>{l.level_name}</option>
                ))}
              </Select>
            </div>
            <div>
              <Label>Age Category</Label>
              <Select value={filterAge} onChange={setFilterAge}>
                <option value="">All Ages</option>
                {(filters?.age_categories || []).map(a => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </Select>
            </div>
          </div>

          <Btn onClick={handleDownload} disabled={downloading} variant="primary">
            {downloading ? "⏳ Preparing..." : "⬇️  Download Excel Template"}
          </Btn>
        </div>

        {/* ── RIGHT: Upload ───────────────────────────────────────────────── */}
        <div style={{ background: C.card, borderRadius: 12, border: `1px solid ${C.border}`,
                      padding: 24, display: "flex", flexDirection: "column", gap: 18 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.navy, display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 20 }}>📤</span> Step 2 — Upload Filled Template
          </div>

          {/* Session label + date */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 12 }}>
            <div>
              <Label>Session Label *</Label>
              <input
                type="text"
                value={sessionLabel}
                onChange={e => setSessionLabel(e.target.value)}
                placeholder="e.g. July 2026 Assessment"
                style={{
                  width: "100%", padding: "8px 10px", borderRadius: 6,
                  border: `1px solid ${sessionLabel ? C.border : C.amber}`,
                  fontSize: 13, boxSizing: "border-box",
                }}
              />
            </div>
            <div>
              <Label>Session Date</Label>
              <input
                type="date"
                value={sessionDate}
                onChange={e => setSessionDate(e.target.value)}
                style={{
                  padding: "8px 10px", borderRadius: 6,
                  border: `1px solid ${C.border}`, fontSize: 13,
                }}
              />
            </div>
          </div>

          {/* Drop zone */}
          <div
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => fileInputRef.current?.click()}
            style={{
              border: `2px dashed ${dragOver ? C.blue : C.border}`,
              borderRadius: 10, padding: "32px 20px", textAlign: "center",
              background: dragOver ? C.blueLight : "#FAFBFD",
              cursor: "pointer", transition: "all 0.15s",
            }}
          >
            <div style={{ fontSize: 32, marginBottom: 8 }}>📂</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.navy }}>
              Drop your filled .xlsx here
            </div>
            <div style={{ fontSize: 12, color: C.textMuted, marginTop: 4 }}>
              or click to browse
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx"
              onChange={onFileChange}
              style={{ display: "none" }}
            />
          </div>

          {uploading && (
            <div style={{ textAlign: "center", color: C.blue, fontSize: 13, fontWeight: 600 }}>
              ⏳ Uploading and saving scores...
            </div>
          )}

          {uploadResult && (
            <div style={{ background: C.greenLight, border: `1px solid ${C.green}`,
                          borderRadius: 8, padding: "12px 16px" }}>
              <div style={{ fontWeight: 700, color: C.green, marginBottom: 4 }}>✅ Upload Successful</div>
              <div style={{ fontSize: 13, color: "#166534" }}>
                <b>{uploadResult.students_updated}</b> students updated &nbsp;·&nbsp;
                <b>{uploadResult.scores_saved}</b> scores saved
              </div>
            </div>
          )}

          {uploadError && (
            <div style={{ background: C.redLight, border: `1px solid ${C.red}`,
                          borderRadius: 8, padding: "12px 16px" }}>
              <div style={{ fontWeight: 700, color: C.red, marginBottom: 2 }}>❌ Upload Failed</div>
              <div style={{ fontSize: 13, color: "#991b1b" }}>{uploadError}</div>
            </div>
          )}
        </div>
      </div>

      {/* ── Upload history ──────────────────────────────────────────────────── */}
      <div style={{ background: C.card, borderRadius: 12, border: `1px solid ${C.border}`,
                    padding: 24, marginTop: 20 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: C.navy, marginBottom: 16 }}>
          📋 Upload History
        </div>

        {sessions.length === 0 ? (
          <div style={{ color: C.textMuted, fontSize: 13, textAlign: "center", padding: "20px 0" }}>
            No uploads yet. Download a template and upload your first performance data.
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr>
                {[
                  { label: "Session Label", key: "session_label" },
                  { label: "Date", key: "session_date" },
                  { label: "Uploaded By", key: "uploaded_by" },
                  { label: "Students", key: "student_count" },
                  { label: "Scores", key: "score_count" },
                  { label: "Created", key: "created_at" },
                ].map(h => (
                  <th key={h.key} onClick={() => toggleSessionSort(h.key)}
                      style={{ textAlign: "left", padding: "8px 12px",
                                       borderBottom: `2px solid ${C.border}`, color: C.navy,
                                       fontWeight: 700, fontSize: 11, textTransform: "uppercase",
                                       cursor: "pointer", userSelect: "none" }}>
                    {h.label}{sessionSortArrow(h.key)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedSessions.map((s, i) => (
                <tr key={s.session_id}
                    style={{ background: i % 2 === 0 ? "#FAFBFD" : C.card }}>
                  <td style={{ padding: "10px 12px", fontWeight: 600, color: C.navy }}>
                    {s.session_label}
                  </td>
                  <td style={{ padding: "10px 12px", color: C.textPrimary }}>
                    {fmtDate(s.session_date)}
                  </td>
                  <td style={{ padding: "10px 12px", color: C.textMuted }}>
                    {s.uploaded_by}
                  </td>
                  <td style={{ padding: "10px 12px" }}>
                    <Badge color={C.blue}>{s.student_count} students</Badge>
                  </td>
                  <td style={{ padding: "10px 12px" }}>
                    <Badge color={C.green}>{s.score_count} scores</Badge>
                  </td>
                  <td style={{ padding: "10px 12px", color: C.textMuted, fontSize: 12 }}>
                    {fmtDateTime(s.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Step 3: Default Assessment ──────────────────────────────────────── */}
      <div style={{ background: C.card, borderRadius: 12, border: `1px solid ${C.border}`,
                    padding: 24, marginTop: 20 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: C.navy, marginBottom: 4,
                      display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 20 }}>⚡</span> Step 3 — Apply Default Assessment Scores
        </div>
        <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 16, lineHeight: 1.6 }}>
          One-click bulk fill: assigns a default score to <b>every active student</b> based on their skill level.
          All indicators get the same score per level. Coach Notes set to "Default assessment".
        </div>

        {/* Score mapping table */}
        <div style={{ display: "flex", gap: 10, marginBottom: 18, flexWrap: "wrap" }}>
          {[
            { level: "Beginner",     score: 1,  color: "#64748b" },
            { level: "Intermediate", score: 4,  color: C.blue    },
            { level: "Advanced",     score: 8,  color: C.green   },
            { level: "Elite",        score: 10, color: "#7c3aed" },
          ].map(({ level, score, color }) => (
            <div key={level} style={{
              background: color + "12", border: `1.5px solid ${color}44`,
              borderRadius: 10, padding: "10px 20px", textAlign: "center", minWidth: 110,
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, color, textTransform: "uppercase",
                            letterSpacing: 0.5, marginBottom: 2 }}>{level}</div>
              <div style={{ fontSize: 26, fontWeight: 800, color }}>{score}</div>
              <div style={{ fontSize: 10, color: C.textMuted }}>/ 10</div>
            </div>
          ))}
        </div>

        {/* Session label + date */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 12, marginBottom: 14 }}>
          <div>
            <Label>Session Label *</Label>
            <input
              type="text"
              value={defaultLabel}
              onChange={e => setDefaultLabel(e.target.value)}
              placeholder="e.g. Default Assessment — Aug 2026"
              style={{
                width: "100%", padding: "8px 10px", borderRadius: 6,
                border: `1px solid ${defaultLabel ? C.border : C.amber}`,
                fontSize: 13, boxSizing: "border-box",
              }}
            />
          </div>
          <div>
            <Label>Performance Date</Label>
            <input
              type="date"
              value={defaultDate}
              onChange={e => setDefaultDate(e.target.value)}
              style={{ padding: "8px 10px", borderRadius: 6,
                       border: `1px solid ${C.border}`, fontSize: 13 }}
            />
          </div>
        </div>

        <Btn
          onClick={() => {
            setDefaultError(null);
            setDefaultResult(null);
            setShowDefaultConfirm(true);
          }}
          disabled={defaultRunning || !defaultLabel.trim()}
          variant="success"
        >
          {defaultRunning ? "⏳ Applying..." : "⚡ Apply Default Scores to All Students"}
        </Btn>

        {defaultResult && (
          <div style={{ background: C.greenLight, border: `1px solid ${C.green}`,
                        borderRadius: 8, padding: "12px 16px", marginTop: 14 }}>
            <div style={{ fontWeight: 700, color: C.green, marginBottom: 4 }}>✅ Default Scores Applied</div>
            <div style={{ fontSize: 13, color: "#166534" }}>
              <b>{defaultResult.students_updated}</b> students updated &nbsp;·&nbsp;
              <b>{defaultResult.scores_saved}</b> scores saved
            </div>
          </div>
        )}

        {defaultError && (
          <div style={{ background: C.redLight, border: `1px solid ${C.red}`,
                        borderRadius: 8, padding: "12px 16px", marginTop: 14 }}>
            <div style={{ fontWeight: 700, color: C.red, marginBottom: 2 }}>❌ Error</div>
            <div style={{ fontSize: 13, color: "#991b1b" }}>{defaultError}</div>
          </div>
        )}
      </div>

      {/* ── Confirmation modal ──────────────────────────────────────────────── */}
      {showDefaultConfirm && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
        }}>
          <div style={{ background: C.card, borderRadius: 14, padding: 32, maxWidth: 420,
                        width: "90%", boxShadow: "0 20px 60px rgba(0,0,0,0.25)" }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: C.navy, marginBottom: 10 }}>
              Confirm Default Assessment
            </div>
            <div style={{ fontSize: 13, color: C.textPrimary, lineHeight: 1.7, marginBottom: 8 }}>
              This will set default scores for <b>all active students</b> on <b>{defaultDate}</b>:
            </div>
            <div style={{ fontSize: 13, color: C.textMuted, lineHeight: 1.8,
                          background: C.bg, borderRadius: 8, padding: "10px 14px", marginBottom: 18 }}>
              Beginner → <b>1</b> &nbsp;·&nbsp; Intermediate → <b>4</b><br/>
              Advanced → <b>8</b> &nbsp;·&nbsp; Elite → <b>10</b><br/>
              Coach Notes → <b>"Default assessment"</b>
            </div>
            <div style={{ fontSize: 12, color: C.green, marginBottom: 20,
                          background: C.greenLight, borderRadius: 6, padding: "8px 12px" }}>
              ✅ Existing scores are preserved — only missing scores will be filled in.
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <Btn variant="ghost" onClick={() => setShowDefaultConfirm(false)}>Cancel</Btn>
              <Btn variant="success" onClick={handleApplyDefaults}>Yes, Apply Default Scores</Btn>
            </div>
          </div>
        </div>
      )}

      {/* ── Student portal info ─────────────────────────────────────────────── */}
      <div style={{ background: C.amberLight, borderRadius: 12, border: `1px solid ${C.amber}55`,
                    padding: 18, marginTop: 20, display: "flex", gap: 14, alignItems: "flex-start" }}>
        <span style={{ fontSize: 24 }}>🔗</span>
        <div>
          <div style={{ fontWeight: 700, color: C.amber, fontSize: 13, marginBottom: 4 }}>
            Student Performance Portal
          </div>
          <div style={{ fontSize: 12, color: "#92400e", lineHeight: 1.6 }}>
            After uploading, students and parents can view performance via their portal link.
            Share the link: <code style={{ background: "#FEF3C7", padding: "1px 6px",
            borderRadius: 4 }}>{window.location.origin}/#/portal/performance/{"<student_code>"}</code>
            &nbsp;— or they can log in to the parent portal to view the dashboard.
          </div>
        </div>
      </div>

    </div>
  );
}
